import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {Formik, Form} from 'formik';
import {map} from 'lodash';
import classnames from 'classnames';
import Label from 'shared/components/label';
import FormikDropdown from 'src/components/formik-dropdown';
import Button from 'src/components/button';
import {US_ABBV_MAP} from 'shared/utils';
import AccessibleTypeahead from 'shared/components/accessible-typeahead';
import {flashMessage, errorMessage} from 'shared/actions/notifications';
import AdminAccessOrganizationService, {
  AdminAccessOrganization,
} from 'src/services/AdminAccessOrganizationService';
import OrganizationFormFields from './organization-form-fields';
import OrganizationsTable from './organizations-table';
import DeleteOrganizationConfirmationModal from './delete-organization-confirmation-modal';
import styles from './styles.module.scss';

const SEARCH_STATE_FILTER_OPTIONS = [
  {value: '', text: 'All States'},
  ...map(US_ABBV_MAP, (name, value) => ({
    value,
    text: name,
  })),
];

interface SearchFormValues {
  state: string;
  name: string;
}

const AccessOrganizationsIndex = () => {
  const dispatch = useDispatch();
  const [organizations, setOrganizations] = useState<AdminAccessOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<AdminAccessOrganization | null>(
    null
  );

  const searchOrganizationsByName = async (query: string) => {
    const response = await AdminAccessOrganizationService.index({name: query});

    return response.map((org) => ({
      text: org.display_name || org.name,
      value: org._id,
      ...org,
    }));
  };

  const onSearch = async (values: SearchFormValues) => {
    setLoading(true);
    try {
      const queryParams: {state?: string; name?: string} = {};
      if (values.state) queryParams.state = values.state;
      if (values.name) queryParams.name = values.name;

      const response = await AdminAccessOrganizationService.index(queryParams);

      setOrganizations(response);
    } catch (error) {
      console.error('Search failed:', error);
      dispatch(errorMessage('Failed to search organizations'));
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async (values: AdminAccessOrganization) => {
    try {
      const created = await AdminAccessOrganizationService.create({access_organization: values});

      dispatch(flashMessage('Access Organization was successfully created.'));
      setOrganizations((orgs) => [created, ...orgs]);
    } catch (error) {
      console.error('Create failed:', error);
      dispatch(errorMessage('Failed to create organization'));
    }
  };

  const onDeleteClick = (org: AdminAccessOrganization) => {
    setOrganizationToDelete(org);
  };

  const onDeleteConfirm = async () => {
    if (!organizationToDelete?._id) return;

    try {
      await AdminAccessOrganizationService.destroy(organizationToDelete._id);

      dispatch(flashMessage('Access Organization was successfully destroyed.'));
      setOrganizations((orgs) => orgs.filter((org) => org._id !== organizationToDelete._id));
    } catch (error: $TSFixMe) {
      console.error('Delete failed:', error);
      const errorMsg = error?.response?.body?.errors?.join(', ') || 'Failed to delete organization';
      dispatch(errorMessage(errorMsg));
    } finally {
      setOrganizationToDelete(null);
    }
  };

  return (
    <div className="access-organizations-index">
      <h2>Find Access Organizations by State or Name</h2>

      <Formik initialValues={{state: '', name: ''}} onSubmit={onSearch}>
        {({setFieldValue, values, isValid, submitCount}) => (
          <Form className="search-form">
            <div className={classnames('form-group', styles.searchFormGroup)}>
              <div className={styles.searchFormColumn}>
                <Label>
                  State
                  <FormikDropdown name="state" options={SEARCH_STATE_FILTER_OPTIONS} />
                </Label>
              </div>
              <div className={styles.searchFormColumn}>
                <Label htmlFor="org-name-typeahead">
                  Access Organization Name
                  <AccessibleTypeahead
                    id="org-name-typeahead"
                    placeholder="Search for organization by name..."
                    onSearch={searchOrganizationsByName}
                    onSelect={(selection) => {
                      const name = typeof selection === 'string' ? selection : selection.text;
                      setFieldValue('name', name);
                    }}
                    value={values.name}
                    minLength={2}
                  />
                </Label>
              </div>
              <div className={styles.searchButton}>
                <Button
                  type="submit"
                  disabled={loading || (!isValid && submitCount > 0)}
                  width="100%"
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <OrganizationsTable organizations={organizations} onDelete={onDeleteClick} />

      <h2>New Access Organization</h2>
      <OrganizationFormFields onSubmit={onCreate} />

      <DeleteOrganizationConfirmationModal
        organization={organizationToDelete}
        onConfirm={onDeleteConfirm}
        onCancel={() => setOrganizationToDelete(null)}
      />
    </div>
  );
};

export default AccessOrganizationsIndex;
